/**
 * CockpitHeader — Zona 1 do cockpit (EntityHeader).
 *
 * Voltar ao CRM + titulo (titulo + numero) + badges (etapa, temperatura,
 * origem) + primaryAction "Criar orcamento" + kebab (arquivar/desarquivar,
 * mover etapa). Toda logica de acao fica no LeadCockpit; aqui so monta o
 * EntityHeader a partir do lead + callbacks.
 *
 * Cada acao do kebab segue o portao do SEU endpoint (CRM-23): "Mover etapa"
 * vem pronto do LeadCockpit em `podeMoverEtapa` (lead:mover + regra do
 * pipeline + lead aberto — ganho e perdido nao mudam de etapa, CRM-07);
 * arquivar exige lead:edit; reverter perda, lead:perder; excluir, lead:delete.
 */
import React, { useMemo } from "react";
import { FiArchive, FiRotateCcw, FiShuffle, FiTrash2, FiZap } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import {
  ORIGEM_LEAD_LABEL,
  STATUS_LEAD_LABEL,
  STATUS_LEAD_TONE,
  TEMPERATURA_LABEL,
  TEMPERATURA_TONE,
} from "../../../services/crm";

function CockpitHeader({
  lead,
  etapaNome,
  onVoltar,
  onCriarOrcamento,
  onMoverEtapa,
  onArquivar,
  onExcluir,
  onReativarPerda,
  podeCriarOrcamento,
  podeEditar,
  podeExcluir,
  podeMoverEtapa = false,
  podeRegistrarPerda,
}) {
  const badges = useMemo(() => {
    const out = [];
    if (etapaNome) {
      out.push({ label: etapaNome, tone: "info", key: "etapa" });
    }
    if (lead.temperatura) {
      out.push({
        label: lead.temperatura_display || TEMPERATURA_LABEL[lead.temperatura] || lead.temperatura,
        tone: TEMPERATURA_TONE[lead.temperatura] || "neutral",
        key: "temp",
      });
    }
    if (lead.status) {
      out.push({
        label: lead.status_display || STATUS_LEAD_LABEL[lead.status] || lead.status,
        tone: STATUS_LEAD_TONE[lead.status] || "neutral",
        key: "status",
      });
    }
    const origemLabel =
      lead.origem_display || ORIGEM_LEAD_LABEL[lead.origem] || lead.origem;
    if (origemLabel) {
      out.push({ label: origemLabel, tone: "neutral", key: "origem" });
    }
    if (lead.arquivado) {
      out.push({ label: "Arquivado", tone: "warning", key: "arq" });
    }
    if (lead.perdido_em) {
      out.push({
        label: lead.motivo_perda_display
          ? `Perdido — ${lead.motivo_perda_display}`
          : "Perdido",
        tone: "danger",
        key: "perdido",
      });
    }
    // Tags livres do lead tambem sao flags (VIP, Reforma, etc.).
    (lead.tags || []).forEach((t, i) => {
      if (t) out.push({ label: t, tone: "neutral", key: `tag-${i}` });
    });
    return out;
  }, [lead, etapaNome]);

  const acoes = useMemo(() => {
    const out = [];
    if (podeMoverEtapa) {
      out.push({
        label: "Mover etapa",
        icon: <FiShuffle size={14} aria-hidden="true" />,
        onClick: onMoverEtapa,
        key: "mover",
      });
    }
    if (podeEditar) {
      out.push({
        label: lead.arquivado ? "Desarquivar lead" : "Arquivar lead",
        icon: lead.arquivado ? (
          <FiRotateCcw size={14} aria-hidden="true" />
        ) : (
          <FiArchive size={14} aria-hidden="true" />
        ),
        onClick: onArquivar,
        danger: !lead.arquivado,
        key: "arquivar",
      });
    }
    // Lead perdido: oferecer reativar (devolve ao board). Reversao da perda.
    if (podeRegistrarPerda && lead.perdido_em) {
      out.push({
        label: "Reverter perda",
        icon: <FiRotateCcw size={14} aria-hidden="true" />,
        onClick: onReativarPerda,
        key: "reativar-perda",
      });
    }
    if (podeExcluir) {
      out.push({
        label: "Excluir lead",
        icon: <FiTrash2 size={14} aria-hidden="true" />,
        onClick: onExcluir,
        danger: true,
        key: "excluir",
      });
    }
    return out;
  }, [
    lead.arquivado,
    lead.perdido_em,
    podeEditar,
    podeExcluir,
    podeMoverEtapa,
    podeRegistrarPerda,
    onMoverEtapa,
    onArquivar,
    onExcluir,
    onReativarPerda,
  ]);

  const primaryAction = podeCriarOrcamento
    ? {
        label: "Criar orçamento",
        icon: <FiZap size={14} aria-hidden="true" />,
        onClick: onCriarOrcamento,
      }
    : undefined;

  // Principal = Nome + Sobrenome do lead (fallback: cliente vinculado, depois
  // a descricao). Subtitulo = descricao do lead + codigo (mono).
  const nomePrincipal =
    [lead.contato_nome, lead.contato_sobrenome].filter(Boolean).join(" ").trim() ||
    lead.cliente_nome ||
    lead.titulo ||
    "Lead";
  // V9: a descricao do lead e campo livre e, na pratica, a equipe preenche
  // com o nome da pessoa. O cabecalho entao repetia o mesmo nome duas vezes
  // ("Tst Jorge Almeida Tst Jorge"). Quando a descricao nao acrescenta nada
  // ao nome, o subtitulo fica so com o codigo.
  const descricaoRedundante = (() => {
    const t = (lead.titulo || "").trim().toLowerCase();
    const n = nomePrincipal.trim().toLowerCase();
    if (!t) return true;
    return t === n || n.includes(t) || t.includes(n);
  })();
  const descricao = descricaoRedundante ? "" : lead.titulo;

  const subtitulo = (
    <>
      {descricao ? <span>{descricao}</span> : null}
      {descricao && lead.numero ? <span aria-hidden="true"> · </span> : null}
      {lead.numero ? (
        <span style={{ fontFamily: "var(--font-mono)" }}>{lead.numero}</span>
      ) : null}
    </>
  );

  return (
    <EntityHeader
      onBack={onVoltar}
      backLabel="CRM"
      title={nomePrincipal}
      subtitle={subtitulo}
      badges={badges}
      primaryAction={primaryAction}
      actions={acoes}
    />
  );
}

export default CockpitHeader;
